import React, {useEffect, useMemo, useState} from "react";
import {Button, Form} from "react-bootstrap";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import {toastError, toastSuccess} from "../../../helpers/Toast";
import "../../../css/ScheduleNotificationCenter.css";

const deliveryLabels = {
  NOT_REQUESTED: "nie wybrano",
  QUEUED: "w kolejce",
  SENT: "wysłano",
  DELIVERED: "dostarczono",
  UNDELIVERED: "niedostarczono",
  FAILED: "błąd wysyłki",
  NOT_CONFIGURED: "brak konfiguracji",
  NO_ADDRESS: "brak adresu"
};

const notificationTypeLabels = {
  SCHEDULE_PUBLISHED: "Publikacja grafiku",
  SCHEDULE_UPDATED: "Zmiana danych grafiku",
  SCHEDULE_ARCHIVED: "Wycofanie grafiku",
  SHIFT_ADDED: "Dodana zmiana",
  SHIFT_UPDATED: "Zmiana grafiku",
  SHIFT_REMOVED: "Usunięta zmiana",
  REQUEST_APPROVED: "Wniosek zaakceptowany",
  REQUEST_REJECTED: "Wniosek odrzucony",
  SWAP_PROPOSED: "Propozycja zamiany",
  SWAP_ACCEPTED: "Zamiana zaakceptowana",
  SWAP_REJECTED: "Zamiana odrzucona",
  SWAP_CANCELLED: "Zamiana anulowana",
  HR_MEETING_INVITATION: "Zaproszenie na spotkanie HR",
  HR_MEETING_UPDATED: "Zmiana spotkania HR",
  HR_MEETING_CANCELLED: "Odwołanie spotkania HR",
  MANUAL: "Wiadomość od kierownika"
};

const dateTime = value => value ? new Intl.DateTimeFormat("pl-PL", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
}).format(new Date(value)) : "—";

const periodLabel = schedule => `${schedule.dateFrom} – ${schedule.dateTo}`;

const statusClass = status => `schedule-notification-center-status schedule-notification-center-status-${String(status || "NOT_REQUESTED").toLowerCase()}`;

const ScheduleNotificationCenter = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [scope, setScope] = useState("ASSIGNED");
  const [employeeId, setEmployeeId] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [historyFilter, setHistoryFilter] = useState("ALL");

  const publishedSchedules = useMemo(
    () => schedules
      .filter(item => item.status === "PUBLISHED")
      .sort((a, b) => `${b.dateFrom}-${b.id}`.localeCompare(`${a.dateFrom}-${a.id}`)),
    [schedules]
  );

  const departments = useMemo(() => {
    const map = new Map();
    publishedSchedules.forEach(item => {
      if (item.departmentId != null) map.set(item.departmentId, item.departmentName || item.departmentShortName || `Dział ${item.departmentId}`);
    });
    return Array.from(map.entries()).map(([id, name]) => ({id, name})).sort((a, b) => a.name.localeCompare(b.name, "pl"));
  }, [publishedSchedules]);

  const visibleSchedules = useMemo(
    () => departmentFilter ? publishedSchedules.filter(item => String(item.departmentId) === departmentFilter) : publishedSchedules,
    [publishedSchedules, departmentFilter]
  );

  const selectedSchedule = useMemo(
    () => publishedSchedules.find(item => String(item.id) === String(selectedScheduleId)) || null,
    [publishedSchedules, selectedScheduleId]
  );

  const visibleHistory = useMemo(() => {
    if (historyFilter === "UNACKNOWLEDGED") return history.filter(item => !item.acknowledgedAt);
    if (historyFilter === "UNREAD") return history.filter(item => !item.readAt);
    if (historyFilter === "SMS_PROBLEM") return history.filter(item => ["FAILED", "UNDELIVERED", "NO_ADDRESS", "NOT_CONFIGURED"].includes(item.smsStatus));
    return history;
  }, [history, historyFilter]);

  const summary = useMemo(() => ({
    total: history.length,
    acknowledged: history.filter(item => item.acknowledgedAt).length,
    smsDelivered: history.filter(item => item.smsStatus === "DELIVERED").length,
    deliveryProblems: history.filter(item => ["FAILED", "UNDELIVERED", "NO_ADDRESS", "NOT_CONFIGURED"].includes(item.smsStatus) || ["FAILED", "NO_ADDRESS", "NOT_CONFIGURED"].includes(item.emailStatus)).length
  }), [history]);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/work-schedules");
      const data = Array.isArray(response.data) ? response.data : [];
      setSchedules(data);
      const published = data.filter(item => item.status === "PUBLISHED").sort((a, b) => `${b.dateFrom}-${b.id}`.localeCompare(`${a.dateFrom}-${a.id}`));
      setSelectedScheduleId(current => current && published.some(item => String(item.id) === String(current)) ? current : (published[0]?.id ? String(published[0].id) : ""));
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać opublikowanych grafików.");
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleData = async schedule => {
    if (!schedule) {
      setEmployees([]);
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const [historyResponse, calendarResponse] = await Promise.all([
        axiosPrivate.get(`/schedule-notifications/schedule/${schedule.id}`),
        axiosPrivate.get(`/work-schedules/${schedule.id}/calendar`, {params: {dateFrom: schedule.dateFrom, dateTo: schedule.dateTo}})
      ]);
      setHistory(Array.isArray(historyResponse.data) ? historyResponse.data : []);
      setEmployees(Array.isArray(calendarResponse.data?.employees) ? calendarResponse.data.employees : []);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać historii powiadomień.");
      setHistory([]);
      setEmployees([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadSchedules();
  }, [axiosPrivate]);

  useEffect(() => {
    loadScheduleData(selectedSchedule);
    setScope("ASSIGNED");
    setEmployeeId("");
    setTitle("");
    setMessage("");
    setHistoryFilter("ALL");
  }, [selectedSchedule?.id]);

  useEffect(() => {
    if (departmentFilter && selectedSchedule && String(selectedSchedule.departmentId) !== departmentFilter) {
      const first = visibleSchedules[0];
      setSelectedScheduleId(first ? String(first.id) : "");
    }
  }, [departmentFilter, selectedSchedule?.departmentId, visibleSchedules]);

  const send = async event => {
    event.preventDefault();
    if (!selectedSchedule) {
      toastError("Wybierz opublikowany grafik.");
      return;
    }
    if (scope === "EMPLOYEE" && !employeeId) {
      toastError("Wybierz pracownika.");
      return;
    }

    setSending(true);
    try {
      const response = await axiosPrivate.post(`/schedule-notifications/schedule/${selectedSchedule.id}/send`, {
        scope,
        employeeId: scope === "EMPLOYEE" ? Number(employeeId) : null,
        title,
        message
      });
      const count = Array.isArray(response.data) ? response.data.length : 0;
      toastSuccess(`Utworzono ${count} ${count === 1 ? "powiadomienie" : "powiadomień"}.`);
      setTitle("");
      setMessage("");
      await loadScheduleData(selectedSchedule);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się wysłać powiadomień.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div id="layoutSidenav_content"><div className="container-fluid px-4"><LoadingDataDiv/></div></div>;

  return (
    <div id="layoutSidenav_content" className="schedule-notification-center-page">
      <div className="container-fluid px-4 py-3">
        <div className="schedule-notification-center-heading">
          <div>
            <h4>Powiadomienia grafików</h4>
            <p>Wysyłka indywidualna, do osób przypisanych do grafiku albo do całego działu oraz kontrola odczytu, potwierdzenia i dostarczenia SMS.</p>
          </div>
          <Button variant="outline-secondary" onClick={loadSchedules}>Odśwież grafiki</Button>
        </div>

        {publishedSchedules.length === 0 ? (
          <div className="schedule-notification-center-empty">Brak opublikowanych grafików dostępnych do wysyłki.</div>
        ) : (
          <>
            <div className="schedule-notification-center-selector">
              <Form.Group>
                <Form.Label>Dział</Form.Label>
                <Form.Select value={departmentFilter} onChange={event => setDepartmentFilter(event.target.value)}>
                  <option value="">Wszystkie dostępne działy</option>
                  {departments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Opublikowany grafik</Form.Label>
                <Form.Select value={selectedScheduleId} onChange={event => setSelectedScheduleId(event.target.value)}>
                  {visibleSchedules.map(item => (
                    <option key={item.id} value={item.id}>{item.departmentName} · {item.name} · {periodLabel(item)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {selectedSchedule && (
              <>
                <div className="schedule-notification-center-context">
                  <strong>{selectedSchedule.departmentName} · {selectedSchedule.name}</strong>
                  <span>{periodLabel(selectedSchedule)}</span>
                  <span>{selectedSchedule.assignmentCount} pozycji grafiku</span>
                </div>

                <form className="schedule-notification-center-send" onSubmit={send}>
                  <div className="schedule-notification-center-send-grid">
                    <Form.Group>
                      <Form.Label>Odbiorcy</Form.Label>
                      <Form.Select value={scope} onChange={event => setScope(event.target.value)}>
                        <option value="ASSIGNED">Osoby przypisane do tego grafiku</option>
                        <option value="DEPARTMENT">Wszyscy pracownicy działu</option>
                        <option value="EMPLOYEE">Jeden pracownik działu</option>
                      </Form.Select>
                    </Form.Group>
                    {scope === "EMPLOYEE" && (
                      <Form.Group>
                        <Form.Label>Pracownik</Form.Label>
                        <Form.Select value={employeeId} onChange={event => setEmployeeId(event.target.value)}>
                          <option value="">-- wybierz --</option>
                          {employees.map(employee => <option key={employee.id} value={employee.id}>{employee.fullName || `${employee.firstName || ""} ${employee.lastName || ""}`.trim()}</option>)}
                        </Form.Select>
                      </Form.Group>
                    )}
                    <Form.Group className={scope === "EMPLOYEE" ? "" : "schedule-notification-center-title-wide"}>
                      <Form.Label>Tytuł</Form.Label>
                      <Form.Control value={title} onChange={event => setTitle(event.target.value)} placeholder={`Informacja o grafiku: ${selectedSchedule.name}`} />
                    </Form.Group>
                    <Form.Group className="schedule-notification-center-message">
                      <Form.Label>Wiadomość</Form.Label>
                      <Form.Control as="textarea" rows={4} value={message} onChange={event => setMessage(event.target.value)} placeholder={`Sprawdź aktualny grafik działu ${selectedSchedule.departmentName}.`} />
                    </Form.Group>
                  </div>
                  <div className="schedule-notification-center-send-footer">
                    <small>Qstaff zawsze zapisuje powiadomienie w systemie. E-mail i SMS są wysyłane tylko wtedy, gdy pracownik ma włączony dany kanał komunikacji.</small>
                    <Button type="submit" variant="primary" disabled={sending}>{sending ? "Wysyłanie..." : "Wyślij powiadomienia"}</Button>
                  </div>
                </form>

                <div className="schedule-notification-center-summary">
                  <div><span>Powiadomienia</span><strong>{summary.total}</strong></div>
                  <div><span>Potwierdzone w Qstaff</span><strong>{summary.acknowledged}</strong></div>
                  <div><span>SMS dostarczone</span><strong>{summary.smsDelivered}</strong></div>
                  <div><span>Problemy kanałów</span><strong>{summary.deliveryProblems}</strong></div>
                </div>

                <div className="schedule-notification-center-history-toolbar">
                  <div>
                    <h5>Historia i weryfikacja odbioru</h5>
                    <small>Odczyt i potwierdzenie dotyczą powiadomienia w Qstaff. Status DELIVERED potwierdza dostarczenie SMS przez operatora.</small>
                  </div>
                  <div className="schedule-notification-center-history-actions">
                    <Form.Select value={historyFilter} onChange={event => setHistoryFilter(event.target.value)}>
                      <option value="ALL">Wszystkie</option>
                      <option value="UNACKNOWLEDGED">Bez potwierdzenia odbioru</option>
                      <option value="UNREAD">Nieprzeczytane</option>
                      <option value="SMS_PROBLEM">Problem z SMS</option>
                    </Form.Select>
                    <Button variant="outline-secondary" onClick={() => loadScheduleData(selectedSchedule)} disabled={historyLoading}>Odśwież statusy</Button>
                  </div>
                </div>

                {historyLoading ? <LoadingDataDiv/> : visibleHistory.length === 0 ? (
                  <div className="schedule-notification-center-empty">Brak powiadomień spełniających wybrany filtr.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle schedule-notification-center-table">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Pracownik</th>
                          <th>Powiadomienie</th>
                          <th>Qstaff</th>
                          <th>E-mail</th>
                          <th>SMS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleHistory.map(item => (
                          <tr key={item.id}>
                            <td className="schedule-notification-center-nowrap">{dateTime(item.createdAt)}</td>
                            <td>{item.employeeFullName}</td>
                            <td className="schedule-notification-center-message-cell">
                              <small>{notificationTypeLabels[item.type] || item.type}</small>
                              <strong>{item.title}</strong>
                              <span>{item.message}</span>
                            </td>
                            <td className="schedule-notification-center-qstaff-cell">
                              <span className={item.readAt ? "schedule-notification-center-check yes" : "schedule-notification-center-check no"}>{item.readAt ? `Odczytano ${dateTime(item.readAt)}` : "Nieodczytane"}</span>
                              <span className={item.acknowledgedAt ? "schedule-notification-center-check yes" : "schedule-notification-center-check no"}>{item.acknowledgedAt ? `Potwierdzono ${dateTime(item.acknowledgedAt)}` : "Bez potwierdzenia"}</span>
                            </td>
                            <td><span className={statusClass(item.emailStatus)}>{deliveryLabels[item.emailStatus] || item.emailStatus}</span>{item.emailSentAt && <small>{dateTime(item.emailSentAt)}</small>}</td>
                            <td>
                              <span className={statusClass(item.smsStatus)}>{deliveryLabels[item.smsStatus] || item.smsStatus}</span>
                              {item.smsDeliveredAt && <small>Dostarczono {dateTime(item.smsDeliveredAt)}</small>}
                              {item.smsProviderStatus && <small>Status operatora: {item.smsProviderStatus}</small>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleNotificationCenter;
